'use client';

import useEmblaCarousel from 'embla-carousel-react';
import { useEffect, useMemo, useState } from 'react';
import { usePrevNextSlider } from '@/app/components/sliders/hooks/usePrevNextSlider';
import type { UseCustomizeStateParams } from '@/app/lcv/components/sections/spartan/products/customize/configurator/types';
import type { SpartanCustomizeModel, SpartanCustomizeModelColor, SpartanCustomizePreviewView } from '@/app/lcv/components/sections/spartan/products/customize/types';
import { getSpartanCustomizeDefaultColor, getSpartanCustomizeInitialModel, getSpartanCustomizeInitialSubCategory, getSpartanCustomizeModelsBySubCategory, getSpartanCustomizePreviewOptions, getSpartanCustomizeSubCategories, resolveSpartanCustomizePreviewView, type SpartanCustomizeSubCategory } from '@/app/lcv/components/sections/spartan/products/customize/utils';
export const useCustomizeState = ({
  models,
  defaultModelDocumentId,
  defaultModelsBySubCategory = {},
  defaultSubCategories,
  initialSubCategoryTitle,
  showSubCategoryTabs = true
}: UseCustomizeStateParams) => {
  const subCategories = useMemo(() => getSpartanCustomizeSubCategories(models, defaultSubCategories), [models, defaultSubCategories]);
  const hasSubCategoryTabs = showSubCategoryTabs && subCategories.length > 1;
  const [selectedSubCategory, setSelectedSubCategory] = useState<SpartanCustomizeSubCategory | null>(() => {
    if (!hasSubCategoryTabs) return null;
    return getSpartanCustomizeInitialSubCategory(subCategories, models, defaultModelDocumentId, defaultSubCategories, initialSubCategoryTitle);
  });
  const gradeModels = useMemo(() => getSpartanCustomizeModelsBySubCategory(models, selectedSubCategory?.documentId), [models, selectedSubCategory?.documentId]);
  const initialModel = useMemo(() => getSpartanCustomizeInitialModel(models, defaultModelDocumentId, defaultModelsBySubCategory, selectedSubCategory?.documentId), [models, defaultModelDocumentId, defaultModelsBySubCategory, selectedSubCategory?.documentId]);
  const [selectedModel, setSelectedModel] = useState<SpartanCustomizeModel | null>(initialModel);
  const [lastModelBySubCategory, setLastModelBySubCategory] = useState<Record<string, string>>(() => {
    const subCategoryId = initialModel?.subCategory?.documentId;
    if (!subCategoryId || !initialModel) return {};
    return {
      [subCategoryId]: initialModel.documentId
    };
  });
  const [selectedColor, setSelectedColor] = useState<SpartanCustomizeModelColor | null>(getSpartanCustomizeDefaultColor(initialModel));
  const [selectedView, setSelectedView] = useState<SpartanCustomizePreviewView>('front');
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: 'start',
    duration: 32
  });
  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick
  } = usePrevNextSlider(emblaApi);
  const previewOptions = useMemo(() => getSpartanCustomizePreviewOptions(selectedModel, selectedColor), [selectedModel, selectedColor]);
  const activeView = resolveSpartanCustomizePreviewView(previewOptions, selectedView);
  const activeIndex = Math.max(0, previewOptions.findIndex(option => option.view === activeView));
  const previewSlidesKey = previewOptions.map(option => `${option.view}:${option.src}`).join('|');
  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      const option = previewOptions[emblaApi.selectedScrollSnap()];
      if (!option) return;
      setSelectedView(option.view);
    };
    emblaApi.on('select', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, previewOptions]);
  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.reInit();
    emblaApi.scrollTo(activeIndex, true);
    // Re-init only when slide sources change (grade/color), not on every swipe.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- activeIndex read for jump-after-reInit
  }, [emblaApi, previewSlidesKey]);
  useEffect(() => {
    if (!emblaApi) return;
    if (emblaApi.selectedScrollSnap() === activeIndex) return;
    emblaApi.scrollTo(activeIndex);
  }, [activeIndex, emblaApi]);
  const applyModel = (model: SpartanCustomizeModel | null) => {
    if (!model) return;
    const nextColor = getSpartanCustomizeDefaultColor(model);
    const nextOptions = getSpartanCustomizePreviewOptions(model, nextColor);
    const nextView = resolveSpartanCustomizePreviewView(nextOptions, activeView);
    const subCategoryId = model.subCategory?.documentId;
    if (subCategoryId) {
      setLastModelBySubCategory(previous => ({
        ...previous,
        [subCategoryId]: model.documentId
      }));
    }
    setSelectedModel(model);
    setSelectedColor(nextColor);
    setSelectedView(nextView);
  };
  const handleSubCategoryChange = (documentId: string) => {
    const nextSubCategory = subCategories.find(item => item.documentId === documentId) ?? null;
    if (!nextSubCategory) return;
    setSelectedSubCategory(nextSubCategory);
    const scopedModels = getSpartanCustomizeModelsBySubCategory(models, nextSubCategory.documentId);
    const rememberedId = lastModelBySubCategory[nextSubCategory.documentId];
    const rememberedModel = rememberedId ? scopedModels.find(model => model.documentId === rememberedId) ?? null : null;
    const nextModel = rememberedModel ?? getSpartanCustomizeInitialModel(models, defaultModelDocumentId, defaultModelsBySubCategory, nextSubCategory.documentId);
    applyModel(nextModel);
  };
  const handleModelChange = (model: SpartanCustomizeModel) => {
    applyModel(model);
  };
  const handleColorChange = (color: SpartanCustomizeModelColor) => {
    const nextOptions = getSpartanCustomizePreviewOptions(selectedModel, color);
    const nextView = resolveSpartanCustomizePreviewView(nextOptions, activeView);
    setSelectedColor(color);
    setSelectedView(nextView);
  };
  return {
    subCategories,
    hasSubCategoryTabs,
    selectedSubCategory,
    gradeModels,
    selectedModel,
    selectedColor,
    previewOptions,
    activeView,
    emblaRef,
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
    setSelectedView,
    handleSubCategoryChange,
    handleModelChange,
    handleColorChange
  };
};